/**
 * Resumen «solo minutos» a partir de `matchDetailsData.liveEvents[0].events`.
 * - Sin límite por cantidad: goles, cambios y tarjetas (resumen); minuto a minuto: todos los sucesos de juego.
 * - No se incluyen PERIOD_END en tarjetas (divisor de medio tiempo aparte).
 * - Orden: 2.ª parte primero (minuto desc), divisor, 1.ª parte (minuto desc).
 */

import {
  eventMatchesAway,
  eventMatchesHome,
  eventTeamIdBelongsToMatch,
  getMatchTeamRefs,
  resolveEventSide,
} from '../../utils/matchTeamRefUtils';

const PERIOD_END_TYPES = new Set(['PERIOD_END', 'END_OF_PERIOD']);

/** Misma idea que `RESUMEN_SOLO_EVENT_TYPES` en constants (tipos de tarjeta API). */
const RESUMEN_LIVE_RAW_TYPES = new Set([
  'GOAL',
  'OWN_GOAL',
  'YELLOW_CARD',
  'RED_CARD',
  'RED_2Y',
  'SECOND_YELLOW',
  'SECOND_YELLOW_RED_CARD',
  'SUBSTITUTION_OUT',
]);

/** Fin de periodo / partido (no se pintan como tarjeta de juego). */
export function isPeriodEndEvent(e) {
  if (!e) return false;
  const type = e.type != null ? String(e.type).trim() : '';
  return PERIOD_END_TYPES.has(type);
}

/**
 * Texto del divisor de fin de periodo / partido desde el evento del servicio.
 * @param {object|null|undefined} event
 * @returns {string}
 */
export function getPeriodEndEventLabel(event) {
  if (!event) return '';
  const name = trimEventText(event.name);
  if (name) return name;
  const displayName = trimEventText(event.displayName);
  if (displayName) return displayName;
  return '';
}

/** @deprecated Usar {@link isPeriodEndEvent}. */
export function isPeriodBoundaryEvent(e) {
  return isPeriodEndEvent(e);
}

export function isKickOffEvent(e) {
  if (!e) return false;
  if (e.type === 'KICK_OFF') return true;
  const name = e.name != null ? String(e.name).trim().toLowerCase() : '';
  return name === 'comienzo';
}

/** Entrada suelta duplica el cambio ya cubierto por `SUBSTITUTION_OUT` + `relatedPlayer`. */
export function isSubstitutionInEvent(e) {
  if (!e) return false;
  const type = e.type != null ? String(e.type).trim() : '';
  return type === 'SUBSTITUTION_IN';
}

/** Inicio de periodo: `PERIOD_START`, `KICK_OFF` o nombre «Comienzo». */
export function isPeriodStartEvent(e) {
  if (!e) return false;
  const type = e.type != null ? String(e.type).trim() : '';
  if (type === 'PERIOD_START' || type === 'KICK_OFF') return true;
  return isKickOffEvent(e);
}

export function mapPeriodStartToCard(api, refs) {
  if (!refs || !eventTeamIdBelongsToMatch(api?.teamId, refs)) {
    return null;
  }

  const name = api?.name != null ? String(api.name).trim() : '';
  const label =
    name !== ''
      ? name
      : isKickOffEvent(api)
        ? ''
        : '';

  const resolvedSide = resolveEventSide(api?.teamId, refs);
  if (!resolvedSide) return null;

  return {
    minute: formatMinuteString(api),
    side: resolvedSide,
    type: 'period_start',
    label,
  };
}

/**
 * Sucesos visibles en minuto a minuto: juego con `teamId` válido, inicio de periodo o injury.
 * @param {object} e
 * @param {ReturnType<typeof getMatchTeamRefs>} refs
 */
export function shouldShowInMinutoTimeline(e, refs) {
  if (!e || !refs) return false;
  if (isPeriodEndEvent(e) || isSubstitutionInEvent(e)) return false;
  if (isInjuryTimeEvent(e)) return true;
  if (isPeriodStartEvent(e)) {
    return eventTeamIdBelongsToMatch(e.teamId, refs);
  }
  const type = e.type != null ? String(e.type).trim() : '';
  if (!type) return false;
  return eventTeamIdBelongsToMatch(e.teamId, refs);
}

/** @param {object} e @param {ReturnType<typeof getMatchTeamRefs>} refs */
export function shouldShowInResumenSolo(e, refs) {
  if (!e || !refs) return false;
  if (
    isPeriodEndEvent(e) ||
    isPeriodStartEvent(e) ||
    isSubstitutionInEvent(e) ||
    isInjuryTimeEvent(e)
  ) {
    return false;
  }
  const type = e.type != null ? String(e.type).trim() : '';
  if (!type || !RESUMEN_LIVE_RAW_TYPES.has(type)) return false;
  return eventTeamIdBelongsToMatch(e.teamId, refs);
}

/**
 * Tiempo añadido / injury time: `INJURY_TIME` o fila sin tipo con texto en `minuteDisplay` / `name`
 * (p. ej. mock: type "", minuteDisplay "Tiempo añadido: 2 min.").
 */
export function isInjuryTimeEvent(e) {
  if (!e || isPeriodEndEvent(e) || isPeriodStartEvent(e)) {
    return false;
  }

  const type = e.type != null ? String(e.type).trim() : '';
  if (type === 'INJURY_TIME') {
    return true;
  }

  const displayName = e.displayName != null ? String(e.displayName).trim() : '';
  if (/tiempo\s+a[nñ]?adido|injury\s*time|stoppage/i.test(displayName)) {
    return true;
  }

  const minuteDisplay = e.minuteDisplay != null ? String(e.minuteDisplay).trim() : '';
  if (/tiempo\s+a[nñ]?adido/i.test(minuteDisplay)) {
    return true;
  }

  const name = e.name != null ? String(e.name).trim() : '';
  if (/tiempo\s+a[nñ]?adido/i.test(name)) {
    return true;
  }

  const teamId = e.teamId != null ? String(e.teamId).trim() : '';
  if (!type && !name && !teamId && /tiempo\s+añadido|injury\s*time|stoppage/i.test(minuteDisplay)) {
    return true;
  }

  return false;
}

function trimEventText(value) {
  if (value == null) return '';
  return String(value).trim();
}

/**
 * `minuteDisplay` del match detail trae el texto del tiempo añadido;
 * en live-match-actions suele ser solo el minuto de juego (p. ej. "45").
 */
function isAddedTimeLabelText(text) {
  const s = trimEventText(text);
  if (!s) return false;
  if (/tiempo\s+a[nñ]?adido|injury\s*time|stoppage/i.test(s)) return true;
  if (/:\s*\d+\s*min/i.test(s)) return true;
  return false;
}

/**
 * Etiqueta de tiempo añadido según origen del evento.
 * - match detail: `minuteDisplay` con el texto completo.
 * - live-match-actions (`INJURY_TIME`): `displayName` o `name`; `minuteDisplay` es el minuto.
 * @param {object} api
 * @returns {string}
 */
export function formatAddedTimeLabel(api) {
  const type = trimEventText(api?.type);
  const minuteDisplay = trimEventText(api?.minuteDisplay);
  const displayName = trimEventText(api?.displayName);
  const name = trimEventText(api?.name);

  if (type === 'INJURY_TIME') {
    if (displayName) return displayName;
    if (name) return name;
    if (isAddedTimeLabelText(minuteDisplay)) return minuteDisplay;
    
  }

  if (isAddedTimeLabelText(minuteDisplay)) {
    return minuteDisplay;
  }
  if (displayName) return displayName;
  if (name) return name;
  if (minuteDisplay) return minuteDisplay;

}

export function mapInjuryTimeToAddedTimeRow(api) {
  return {
    minute: null,
    side: 'center',
    type: 'added_time',
    label: formatAddedTimeLabel(api),
  };
}

function isPlayAfterInjuryTime(playApi, injuryApi) {
  const injuryMinute = injuryApi?.minute != null ? Number(injuryApi.minute) : null;
  if (injuryMinute == null || !Number.isFinite(injuryMinute)) {
    return false;
  }
  const playMinute = playApi?.minute != null ? Number(playApi.minute) : null;
  if (playMinute == null || !Number.isFinite(playMinute)) {
    return false;
  }
  if (playMinute > injuryMinute) {
    return true;
  }
  if (playMinute < injuryMinute) {
    return false;
  }
  const injurySecond = injuryApi?.second != null ? Number(injuryApi.second) : 0;
  const playSecond = playApi?.second != null ? Number(playApi.second) : 0;
  return playSecond > injurySecond;
}

export function mapSegmentWithInjuryTimeRows(segmentApis, mapPlayRow) {
  const segmentFiltered = segmentApis.filter((e) => e && !isPeriodEndEvent(e));
  const injuries = segmentFiltered.filter(isInjuryTimeEvent);
  const plays = segmentFiltered.filter((api) => !isInjuryTimeEvent(api));

  if (!injuries.length) {
    return [...plays].sort(sortByMinuteDesc).map(mapPlayRow);
  }

  const injury = injuries.reduce((latest, candidate) =>
    Number(candidate?.minute ?? 0) > Number(latest?.minute ?? 0) ? candidate : latest,
  );

  const stoppagePlays = plays.filter((api) => isPlayAfterInjuryTime(api, injury));
  const regularPlays = plays.filter((api) => !isPlayAfterInjuryTime(api, injury));

  const rows = [];
  const pushPlay = (api) => {
    const row = mapPlayRow(api);
    if (row) rows.push(row);
  };
  stoppagePlays.sort(sortByMinuteDesc).forEach(pushPlay);
  rows.push(mapInjuryTimeToAddedTimeRow(injury));
  regularPlays.sort(sortByMinuteDesc).forEach(pushPlay);
  return rows;
}

export function sortByMinuteDesc(a, b) {
  const ma = a.minute != null ? Number(a.minute) : 0;
  const mb = b.minute != null ? Number(b.minute) : 0;
  if (mb !== ma) return mb - ma;
  const sa = a.second != null ? Number(a.second) : 0;
  const sb = b.second != null ? Number(b.second) : 0;
  return sb - sa;
}

function formatMinuteString(e) {
  const d = e.minuteDisplay;
  if (d != null && String(d).trim() !== '') {
    const s = String(d).trim();
    return /'$/.test(s) ? s : `${s}'`;
  }
  if (e.minute != null) return `${e.minute}'`;
  return "0'";
}

/**
 * Resumen «solo minutos» (live): local → izquierda; visita → derecha.
 * Resuelve `teamId` del evento (MEX/POR o GN*) contra `code` / `teamId` del matchCard.
 */
function sideFromEventTeamIdResumen(teamId, refs) {
  return resolveEventSide(teamId, refs);
}

/**
 * Minuto a minuto (live): misma resolución home/away que resumen.
 */
export function sideFromEventTeamIdForMinuto(teamId, refs) {
  return resolveEventSide(teamId, refs);
}

const API_TYPE_TO_CARD = {
  YELLOW_CARD: 'yellow_card',
  RED_CARD: 'red_card',
  RED_2Y: 'red_card_2y',
  SECOND_YELLOW: 'red_card_2y',
  SECOND_YELLOW_RED_CARD: 'red_card_2y',
  GOAL: 'goal',
  OWN_GOAL: 'own_goal',
  SHOT_OFF_TARGET: 'shot_off',
  SHOT_ON_TARGET: 'shot',
  FREE_KICK: 'free_kick',
  FOUL: 'foul',
  OFFSIDE: 'offside',
  CORNER: 'corner',
  SUBSTITUTION_OUT: 'sub',
  SUBSTITUTION_IN: 'sub',
  BLOCKED_SHOT: 'blocked',
  SAVE: 'save',
  PENALTY_AWARDED: 'free_kick',
  PENALTY_MISSED: 'shot_off',
  VAR: 'foul',
};

/**
 * Clave para el mapa de marcador tras gol: `eventId` solo no basta si el feed repite el mismo id en varios goles.
 */
/**
 * Marcador en tarjeta de gol desde `live-match-actions` (`homeScore` / `awayScore` en el payload).
 * @param {object} api
 * @returns {string|undefined} p. ej. `"1-0"`; sin ambas llaves, no se muestra marcador.
 */
function formatGoalScoreFromApiEvent(api) {
  if (api?.homeScore == null || api?.awayScore == null) {
    return undefined;
  }

  const home = Number(api.homeScore);
  const away = Number(api.awayScore);
  if (Number.isNaN(home) || Number.isNaN(away)) {
    return undefined;
  }

  return `${home}-${away}`;
}

function goalScoreStorageKeyFromApiEvent(e) {
  if (!e || e.eventId == null || String(e.eventId).trim() === '') {
    return null;
  }
  const m = e.minute != null && e.minute !== '' ? String(e.minute) : '';
  const s = e.second != null && e.second !== '' ? String(e.second) : '0';
  return `${String(e.eventId).trim()}@${m}:${s}`;
}

/**
 * @param {object} matchDetailsData
 * @returns {Map<string, string>} clave por gol -> "h-a" tras el gol
 */
export function buildGoalScoreByEventId(rawEvents, refs) {
  const map = new Map();
  let h = 0;
  let a = 0;
  for (const e of rawEvents) {
    if (!e || (e.type !== 'GOAL' && e.type !== 'OWN_GOAL') || isPeriodEndEvent(e)) continue;
    const isOwnGoal = e.type === 'OWN_GOAL';
    if (!eventTeamIdBelongsToMatch(e.teamId, refs)) continue;

    if (isOwnGoal) {
      if (eventMatchesHome(e.teamId, refs)) {
        a += 1;
      } else {
        h += 1;
      }
    } else if (eventMatchesHome(e.teamId, refs)) {
      h += 1;
    } else {
      a += 1;
    }
    const k = goalScoreStorageKeyFromApiEvent(e);
    if (k) {
      map.set(k, `${h}-${a}`);
    }
  }
  return map;
}

/**
 * @param {object} api
 * @param {object} refs — `getMatchTeamRefs(md)`
 * @param {boolean} [forMinutoTimeline]
 */
export function mapOneEventToCard(api, refs, forMinutoTimeline = false) {
  if (!refs || !eventTeamIdBelongsToMatch(api?.teamId, refs)) {
    return null;
  }

  const t = api.type;
  const cardType = API_TYPE_TO_CARD[t] || 'free_kick';
  const side = forMinutoTimeline
    ? sideFromEventTeamIdForMinuto(api.teamId, refs)
    : sideFromEventTeamIdResumen(api.teamId, refs);
  if (!side) return null;
  const minute = formatMinuteString(api);
  const label = api.name != null && String(api.name).trim() !== '' ? String(api.name).trim() : 'Evento';
  const playerName = api.player?.name != null ? String(api.player.name) : '';

  const base = { minute, side, type: cardType, label, player: playerName || undefined };

  if (t === 'OWN_GOAL') {
    return {
      minute,
      side,
      type: 'own_goal',
      label: label || 'Gol en contra',
      player: playerName || undefined,
    };
  }

  if (cardType === 'goal') {
    const out = { ...base, type: 'goal', label, player: playerName };
    const score = formatGoalScoreFromApiEvent(api);
    if (score) {
      out.score = score;
    }
    if (api.relatedPlayer != null && typeof api.relatedPlayer === 'object') {
      out.relatedPlayer = {
        name: api.relatedPlayer.name != null ? String(api.relatedPlayer.name) : '',
      };
    }
    return out;
  }

  if (cardType === 'sub') {
    if (t === 'SUBSTITUTION_OUT') {
      return {
        ...base,
        type: 'sub',
        label: 'Cambio',
        playerIn: api.relatedPlayer?.name != null ? String(api.relatedPlayer.name) : '',
        playerOut: playerName,
      };
    }
    if (t === 'SUBSTITUTION_IN') {
      return {
        ...base,
        type: 'sub',
        label: 'Cambio',
        playerIn: playerName,
        playerOut: api.relatedPlayer?.name != null ? String(api.relatedPlayer.name) : '',
      };
    }
  }

  if (cardType === 'foul' && playerName) {
    return { ...base, type: 'foul', label, player: playerName };
  }

  return base;
}

export function findIndexFirstHalfEnd(raw) {
  const endTypes = new Set(['PERIOD_END', 'END_OF_PERIOD']);
  const byName = raw.findIndex(
    (e) =>
      endTypes.has(e?.type) &&
      /primer|primera|1\.ª|medio|final de tiempo/i.test(getPeriodEndEventLabel(e)),
  );
  if (byName !== -1) return byName;
  const by45 = raw.findIndex(
    (e) => endTypes.has(e?.type) && e?.minute != null && Number(e.minute) <= 47,
  );
  if (by45 !== -1) return by45;
  return raw.findIndex(
    (e) => endTypes.has(e?.type) && e?.minute != null && Number(e.minute) < 90,
  );
}

export function findIndexMatchEnd(raw) {
  const endTypes = new Set(['PERIOD_END', 'END_OF_PERIOD']);
  let last = -1;
  raw.forEach((e, i) => {
    if (endTypes.has(e?.type) && e?.minute != null && Number(e.minute) >= 90) {
      last = i;
    }
  });
  if (last !== -1) return last;
  return raw.findIndex(
    (e) =>
      endTypes.has(e?.type) &&
      /(partido|final del partido|full time|match end|partido finalizado)/i.test(
        getPeriodEndEventLabel(e),
      ),
  );
}

/**
 * Parte el stream en 1.ª y 2.ª mitad. Sin `PERIOD_END` de medio tiempo, todo va a 1.ª parte.
 * @param {object[]} raw
 * @returns {{ halfEnd: number, firstSlice: object[], secondSlice: object[] }}
 */
export function splitLiveEventsByHalf(raw) {
  const halfEnd = findIndexFirstHalfEnd(raw);
  const matchEnd = findIndexMatchEnd(raw);
  const end2h = matchEnd >= 0 ? matchEnd : raw.length;

  if (halfEnd < 0) {
    return {
      halfEnd,
      firstSlice: raw.slice(0, end2h),
      secondSlice: [],
    };
  }

  return {
    halfEnd,
    firstSlice: raw.slice(0, halfEnd),
    secondSlice: raw.slice(halfEnd + 1, end2h),
  };
}

/**
 * Texto de estado del partido solo desde `liveEvents[0]` (bloque y `events`).
 * @param {object|undefined} matchDetailsData
 * @returns {string}
 */
export function getMatchStatusFromLiveEvents(matchDetailsData) {
  const block = matchDetailsData?.liveEvents?.[0];
  if (!block || typeof block !== 'object') return '';
  for (const key of ['matchStatus', 'status', 'phaseLabel', 'statusText']) {
    const v = block[key];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  const raw = block.events;
  if (!Array.isArray(raw)) return '';
  for (let i = raw.length - 1; i >= 0; i -= 1) {
    const e = raw[i];
    if (e?.type !== 'PERIOD_END' && e?.type !== 'END_OF_PERIOD') continue;
    const label = getPeriodEndEventLabel(e);
    const m = e.minute != null && e.minute !== '' ? Number(e.minute) : 0;
    if (
      m >= 90 ||
      /fin del partido|final del partido|full time|match end|partido finalizado/i.test(label)
    ) {
      return label;
    }
  }
  return '';
}

/**
 * @param {object|undefined} matchDetailsData
 * @returns {{ events: object[], fromLive: true, secondHalfCount: number, halftimeLabel: string } | null}
 */
export function mapLiveEventsToResumenSoloData(matchDetailsData) {
  const block = matchDetailsData?.liveEvents?.[0];
  const raw = block?.events;
  if (!Array.isArray(raw) || raw.length === 0) {
    return null;
  }

  const card = matchDetailsData?.matchCard?.[0];
  const md = card?.summary?.matchDetails ?? matchDetailsData?.matchDetails;
  const refs = getMatchTeamRefs(md);

  const { halfEnd, firstSlice, secondSlice } = splitLiveEventsByHalf(raw);
  const firstSegment = firstSlice.filter((e) => shouldShowInResumenSolo(e, refs));
  const secondSegment = secondSlice.filter((e) => shouldShowInResumenSolo(e, refs));

  const firstDesc = [...firstSegment].sort(sortByMinuteDesc);
  const secondDesc = [...secondSegment].sort(sortByMinuteDesc);

  const displayChronoOrder = [...secondDesc, ...firstDesc];

  const events = displayChronoOrder
    .map((api) => mapOneEventToCard(api, refs, false))
    .filter(Boolean);

  if (!events.length) {
    return null;
  }

  const halftimeLabel =
    halfEnd >= 0 ? getPeriodEndEventLabel(raw[halfEnd]) : '';

  return {
    events,
    fromLive: true,
    secondHalfCount: secondDesc.length,
    halftimeLabel,
    showHalftime: halfEnd >= 0 && Boolean(halftimeLabel),
  };
}
